import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import RequiredIconRed from '../../../assets/icons/astrik-red.svg';
import RequiredIcon from '../../../assets/icons/astrik.svg';
import { DEFAULT_HEIGHTS } from '../../../constants/pdfOverlayDimensions';
import { FileContext } from '../../../context/FileContext';
import { useColumnOrder } from '../../../hooks/useColumnOrder';
import { resolveTextDirection } from '../../../utils/common';

const Icon = styled.img``;

const OuterContainer = styled.div`
  position: absolute;
  top: ${({ y }) => y + 'px'};
  left: ${({ x }) => x + 'px'};
  width: ${({ width }) => width + 'px'};
  height: ${({ height }) => height + 'px'};
  pointer-events: auto;
`;
const TextBoxContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const TextBoxInput = styled.textarea`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  cursor: auto;
  padding-left: ${({ noPadding }) => (noPadding ? '0' : '1px')};
  padding-right: ${({ fontSize, itemRequired }) =>
    itemRequired ? fontSize * 1.1 + 'px' : ''};
  font-size: ${({ fontSize }) => `${fontSize}px`};
  line-height: ${({ fontSize }) => fontSize + 'px'};
  border: ${({ required }) =>
    `${required ? '1px solid #D83A52' : '1px solid #c5c7d0'}`};
  border-radius: 4px;
  resize: none;
  background-color: ${({ color }) => (color ? color : '#ffffff')};
  color-scheme: only light;
  forced-color-adjust: none;
  font-family: ${({ fontFamily }) =>
    fontFamily
      ? `'${fontFamily}', 'Open Sans', sans-serif`
      : `'Open Sans', sans-serif`};
  color: ${({ textColor }) => textColor || 'black'};
  -webkit-text-fill-color: ${({ textColor }) => textColor || 'black'};
  /* CSS direction has no "auto"; HTML dir= handles mixed HE+EN BiDi. */
  direction: ${({ direction }) =>
    direction === 'rtl' ? 'rtl' : direction === 'ltr' ? 'ltr' : undefined};
  text-align: ${({ direction }) => (direction === 'rtl' ? 'right' : 'left')};

  ::placeholder {
    color: ${({ required }) => `${required ? 'white' : ''}`};
    -webkit-text-fill-color: ${({ required }) =>
      `${required ? 'white' : ''}`};
  }

  ::-webkit-scrollbar {
    width: 1px;
  }
`;

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: ${({ fontSize }) => fontSize + 'px'};
  padding-top: ${({ fontSize }) =>
    fontSize * 0.3 +
    'px'}; //icon size is 0.7 times font size so 0.3 padding top and padding bottom each
  z-index: 1;
  height: 100%;
  display: flex;
  background-color: transparent;
  pointer-events: none;
`;

const TextBoxPreviewOverlayChip = ({ x, y, item, from, showColor }) => {
  const { addedFormFields, updateFormFields } = useContext(FileContext);
  const noPadding = item.noPadding || false;
  const padding = noPadding ? 0 : 6;

  useEffect(() => {
    if (!item?.height || item.height < 0) {
      item.height = DEFAULT_HEIGHTS.TEXT_BOX;
      const index = addedFormFields.findIndex((x) => x.id === item.id);
      if (index > -1) {
        addedFormFields[index] = item;
        updateFormFields([...addedFormFields]);
      }
    }
  }, [item, addedFormFields, updateFormFields]);
  const [isRequired, setIsRequired] = useState(false);
  const { mapColor } = useColumnOrder();
  const itemColor = item.color ?? item.signer?.color;
  const backgroundColor = itemColor ? mapColor(itemColor) : '#E6E6FA';

  const handleInput = (e) => {
    item.content && setIsRequired(false);
    if (from !== '/sign') return;
    const selectedItem = addedFormFields.find((x) => x.id === item.id);
    selectedItem.content = e.target.value;
    setIsRequired(false);
    selectedItem.hasError = false;
    updateFormFields([...addedFormFields]);
  };

  useEffect(() => {
    if (item.hasError) {
      setIsRequired(true);
    }
  }, [addedFormFields, item.hasError]);

  // Padding for border and consistent spacing
  const fontSize = item.fontSize || 11;
  const textColor = item?.fontStyle?.color
    ? `rgb(${item.fontStyle.color.r}, ${item.fontStyle.color.g}, ${item.fontStyle.color.b})`
    : undefined;
  const textDirection = resolveTextDirection(item.content || '');

  return (
    <>
      <OuterContainer
        x={x + padding}
        y={y + padding}
        height={item.height}
        width={item.width}
        noPadding={noPadding}
      >
        <TextBoxContainer height={item.height} width={item.width}>
          <TextBoxInput
            id={`text_box_${item.id}`}
            x={x + padding}
            y={y + padding}
            height={item.height}
            width={item.width}
            fontSize={fontSize}
            textColor={textColor}
            required={isRequired}
            itemRequired={item.required}
            onInput={handleInput}
            value={item.content}
            color={showColor ? backgroundColor : ''}
            noPadding={noPadding}
            direction={textDirection}
            dir={textDirection}
          ></TextBoxInput>
          <OverlayContainer fontSize={fontSize} noPadding={noPadding}>
            {item.required ? (
              <Icon
                src={isRequired ? RequiredIconRed : RequiredIcon}
                alt=""
                height={fontSize * 0.7}
                width={fontSize * 0.7}
                noPadding={noPadding}
              />
            ) : null}
          </OverlayContainer>
        </TextBoxContainer>
      </OuterContainer>
    </>
  );
};

export default TextBoxPreviewOverlayChip;
